import React, { useState, useEffect, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';

import { AuditOutlined, DeleteOutlined } from '@ant-design/icons';
import { Input, Select, Row, Col } from 'antd';
import {
  Form,
  StyledModal,
  FormAddButton,
  dialogFunction,
  WrapperSelect,
} from 'common/components';
import RibbonButton from 'common/components/button/RibbonButton';

import gridSelectors from 'common/components/grid-view/controllers/selectors';

import { useDispatchReloadPage } from 'hooks/useReloadPage';
import { useInvalidateAllGDSNProducts } from 'pages/publication/hooks';

import * as productServices from 'services/product';
import { apiHandler } from 'utils/api';
import Messages from 'i18n/messages/home';

import { filterParams, getUniqueReasons, mappingReason } from './utils';

import { CIC_REASON, CIC_REASON_FREE_FORM } from 'static/Constants';

import './index.less';

const { Option } = Select;

const ReviewPublication = (props) => {
  const { onRefetchNavigationMenu, onClearItemGrid } = props;
  const intl = useIntl();

  const [isOpenModalReview, setIsOpenModalReview] = useState(false);
  const [reasons, setReasons] = useState([{ displayText: '' }]);

  const [form] = Form.useForm();

  const reloadPage = useDispatchReloadPage();
  const invalidateAllGDSNReceivedProducts = useInvalidateAllGDSNProducts();

  const uniqueReasons = useMemo(() => {
    return getUniqueReasons(reasons);
  }, [reasons]);

  const gridSelections = useSelector(
    gridSelectors.makeSelectDetailCurrentITemsSelection()
  );

  const isHiddenAddReason = reasons?.length === CIC_REASON.length;

  useEffect(() => {
    if (!isOpenModalReview) {
      setReasons([{ displayText: '' }]);
    }
  }, [isOpenModalReview]);

  const onClickBtn = () => {
    setIsOpenModalReview(true);
  };

  const handleOk = () => {
    form.validateFields().then((values) => {
      const mappingValues = mappingReason(values);
      const filteredValues = filterParams(mappingValues);
      callApiReviewPublication(filteredValues);
    });
  };

  const callApiReviewPublication = (values) => {
    const idList = gridSelections.map((item) => item?.id);
    const params = {
      ids: idList,
      ...values,
    };

    const successMessage = intl.formatMessage(
      Messages.reviewPublicationSuccess
    );
    const errorMessage = intl.formatMessage(Messages.reviewPublicationError);

    apiHandler({
      service: productServices.reviewPublication,
      params,
      successMessage,
      errorMessage,
      successCallback,
    });
  };
  const successCallback = () => {
    form.resetFields();
    reloadPage();
    onRefetchNavigationMenu();
    invalidateAllGDSNReceivedProducts();
    onClearItemGrid();
    setIsOpenModalReview(false);
  };

  const handleCancel = () => {
    form.resetFields();
    setIsOpenModalReview(false);
  };

  const handleRemoveReason = (key) => {
    const newReasons = [...reasons.slice(0, key), ...reasons.slice(key + 1)];
    setReasons(newReasons);
  };

  const checkDisabled = () => {
    if (!gridSelections?.length) return true;

    return gridSelections.some(
      (item) => item?.status?.toLowerCase() !== 'pending'
    );
  };

  const handleChangeReason = (value, key) => {
    let foundReason = reasons[key];

    foundReason = {
      ...foundReason,
      displayText: value,
    };

    const newReasons = [
      ...reasons.slice(0, key),
      foundReason,
      ...reasons.slice(key + 1),
    ];

    setReasons(newReasons);
  };

  return (
    <>
      <RibbonButton
        size='small'
        icon={<AuditOutlined />}
        label={Messages.reviewSubscription}
        onClick={onClickBtn}
        disabled={checkDisabled()}
      />

      {isOpenModalReview && (
        <StyledModal
          title={intl.formatMessage(Messages.reviewGDSNPublication)}
          visible={isOpenModalReview}
          onOk={handleOk}
          onCancel={handleCancel}
          destroyOnClose
        >
          <Form
            name='review-publication'
            autoComplete='off'
            initialValues={{
              gdsnCicReasons: [
                { displayText: null, additionalDescription: '' },
              ],
            }}
            form={form}
            layout='vertical'
            className='review-publication'
          >
            <Form.List name='gdsnCicReasons'>
              {(fields, { add, remove }) => (
                <Row>
                  {fields.map(({ key, name, fieldKey, ...restField }) => (
                    <React.Fragment key={key}>
                      <Col span={24}>
                        <Form.Item
                          {...restField}
                          style={{ width: '100%' }}
                          label={
                            <div
                              style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                width: '100%',
                              }}
                            >
                              <span>CIC Reason</span>
                              {fields?.length > 1 && (
                                <DeleteOutlined
                                  style={{
                                    fontSize: 17,
                                    paddingTop: 2,
                                    paddingLeft: 5,
                                    color: '#ff4d4f',
                                  }}
                                  onClick={() => {
                                    dialogFunction({
                                      type: 'warn',
                                      content:
                                        'Are you sure want to delete the selecting reason?',
                                      onOk: () => {
                                        remove(name);
                                        handleRemoveReason(name);
                                      },
                                    });
                                  }}
                                />
                              )}
                            </div>
                          }
                          name={[name, 'reason']}
                          fieldKey={[fieldKey, 'reason']}
                          rules={[
                            {
                              required: true,
                              message: 'CIC Code is required',
                            },
                          ]}
                        >
                          <WrapperSelect
                            showSearch
                            placeholder='CIC Reason'
                            filterOption={(input, option) =>
                              option.children
                                ?.toLowerCase()
                                .indexOf(input?.toLowerCase()) >= 0
                            }
                            onChange={(value) => {
                              handleChangeReason(value, key);
                            }}
                          >
                            {uniqueReasons.map((item, idx) => (
                              <Option key={idx} value={item.displayText}>
                                {item.displayText}
                              </Option>
                            ))}
                          </WrapperSelect>
                        </Form.Item>
                        {reasons?.[key]?.displayText ===
                          CIC_REASON_FREE_FORM.displayText && (
                          <Form.Item
                            {...restField}
                            label='Additional Description'
                            name={[name, 'additionalDescription']}
                            fieldKey={[fieldKey, 'additionalDescription']}
                          >
                            <Input.TextArea
                              placeholder='Additional Description'
                              rows={4}
                            />
                          </Form.Item>
                        )}
                      </Col>
                    </React.Fragment>
                  ))}

                  {isHiddenAddReason ? null : (
                    <Col span={24}>
                      <FormAddButton
                        text={'Add Field'}
                        style={{ borderRadius: 'none' }}
                        onClick={() => {
                          add();
                          setReasons((prevReasons) =>
                            prevReasons.concat({
                              displayText: '',
                            })
                          );
                        }}
                      />
                    </Col>
                  )}
                </Row>
              )}
            </Form.List>
          </Form>
        </StyledModal>
      )}
    </>
  );
};

export default ReviewPublication;
